import { INDEPENDENCE_DISCLAIMER, PRACTICE_PROXY_NOTICE } from '@titrate/shared';
import { useExams, type ExamInfo } from '../lib/queries';
import { Spinner } from '../components/ui';
import { usePageMeta } from '../lib/pageMeta';
import { useJsonLd } from '../lib/jsonLd';

/**
 * Built from the same exam data the page already renders above, rather than
 * hand-written — a question count or time limit here can never drift from
 * what the domain table says, because both read the one API response.
 *
 * Every answer is plain text, no markup: FAQPage structured data requires it,
 * and it is also what gets rendered, so the visible page and the JSON-LD
 * describing it are guaranteed to agree — a mismatch between the two is
 * exactly what structured-data guidelines exist to catch.
 */
function buildFaq(exams: ExamInfo[]): { question: string; answer: string }[] {
  const caop = exams.find((e) => e.code === 'CAOP');
  const opra = exams.find((e) => e.code === 'OPRA');
  const faq: { question: string; answer: string }[] = [];

  if (caop) {
    faq.push({
      question: 'What is CAOP?',
      answer: `${caop.name} (CAOP) is a restricted open-book exam of ${caop.questionCount} questions in ${Math.round(caop.timeLimitSec / 60)} minutes, sat by overseas-qualified pharmacists seeking registration in Australia.`,
    });
  }
  if (opra) {
    faq.push({
      question: 'What is OPRA?',
      answer: `${opra.name} (OPRA) is a closed-book exam of ${opra.questionCount} questions in ${Math.round(opra.timeLimitSec / 60)} minutes, sat by overseas-qualified pharmacists seeking registration in Australia.`,
    });
  }
  if (caop && opra) {
    faq.push({
      question: 'What is the difference between CAOP and OPRA?',
      answer: `CAOP is restricted open-book (you may use permitted physical reference books) and closed-book for OPRA. CAOP is ${caop.questionCount} questions in ${Math.round(caop.timeLimitSec / 60)} minutes; OPRA is ${opra.questionCount} questions in ${Math.round(opra.timeLimitSec / 60)} minutes. Which one you sit depends on your registration pathway with the Australian Pharmacy Council.`,
    });
  }
  if (caop) {
    faq.push({
      question: 'Is CAOP open book?',
      answer: `CAOP is restricted open-book: ${caop.permittedReferences.length > 0 ? `you may bring physical copies of ${caop.permittedReferences.join(', ')}, and nothing else` : 'a defined, limited set of physical reference books is permitted'}. Reaching for a reference on every question will cost you the paper on time alone.`,
    });
  }
  if (caop || opra) {
    const pace = [caop, opra]
      .filter((e): e is ExamInfo => Boolean(e))
      .map((e) => `${e.code} gives you about ${e.benchmarkPaceSec} seconds a question`)
      .join('; ');
    faq.push({
      question: 'How much time do I get per question in CAOP and OPRA?',
      answer: `${pace}. Running out of time fails candidates as often as not knowing the content, which is why Titrate tracks your pace from your first practice attempt.`,
    });
  }

  faq.push(
    {
      question: 'Does Titrate use real CAOP or OPRA exam questions?',
      answer:
        'No. Every practice question on Titrate is original content written for this platform. No real exam question from the Australian Pharmacy Council is ever reproduced or paraphrased.',
    },
    {
      question: 'Are Titrate scores a prediction of whether I will pass CAOP or OPRA?',
      answer: PRACTICE_PROXY_NOTICE,
    },
    {
      question: 'Is Titrate affiliated with the Australian Pharmacy Council?',
      answer: INDEPENDENCE_DISCLAIMER,
    },
  );

  return faq;
}

export function About() {
  usePageMeta({
    title: 'About the exams',
    description: "The CAOP and OPRA exam structure, straight from the Australian Pharmacy Council's published guides — what every Titrate practice paper is built to match.",
    path: '/about',
  });
  const { data, isLoading } = useExams();
  const faq = data ? buildFaq(data.exams) : undefined;
  useJsonLd(
    'faq-jsonld',
    faq && faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
          })),
        }
      : undefined,
  );
  if (isLoading) return <Spinner />;

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <header>
        <h1 className="text-3xl font-bold tracking-tight text-ink">About the exams</h1>
        <p className="mt-3 leading-relaxed text-muted">
          The Australian Pharmacy Council administers two written assessments for overseas-qualified
          pharmacists seeking registration in Australia. The structure below is taken from APC&apos;s
          published exam guides, and it is what every practice paper on Titrate is built to match.
        </p>
      </header>

      {data?.exams.map((exam) => (
        <section key={exam.code} className="card p-4 sm:p-6">
          <h2 className="text-xl font-bold text-ink">
            {exam.code} <span className="text-base font-medium text-muted">— {exam.name}</span>
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted">{exam.description}</p>

          <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide text-muted">
            Content domains and weightings
          </h3>
          <table className="mt-2.5 w-full text-sm">
            <tbody>
              {exam.domains.map((d) => (
                <tr key={d.code} className="border-b border-line last:border-0">
                  <td className="py-2 pr-4 text-muted">
                    <span className="font-medium text-muted">{d.code}</span> {d.name}
                  </td>
                  <td className="w-20 py-2 text-right font-semibold tabular-nums text-ink">{d.weightPct}%</td>
                </tr>
              ))}
            </tbody>
          </table>

          {exam.permittedReferences.length > 0 ? (
            <>
              <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide text-muted">
                Permitted references
              </h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted">
                {exam.permittedReferences.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
              <p className="mt-2.5 text-xs leading-relaxed text-muted">
                Physical copies only. Titrate does not reproduce the content of these references —
                you study with your own copies, exactly as you will in the exam.
              </p>
            </>
          ) : null}
        </section>
      ))}

      <section className="card p-4 sm:p-6">
        <h2 className="text-base font-semibold text-ink">How results work</h2>
        <p className="mt-2.5 text-sm leading-relaxed text-muted">{PRACTICE_PROXY_NOTICE}</p>
      </section>

      <section className="card p-4 sm:p-6">
        <h2 className="text-base font-semibold text-ink">Independence</h2>
        <p className="mt-2.5 text-sm leading-relaxed text-muted">{INDEPENDENCE_DISCLAIMER}</p>
      </section>

      {faq && faq.length > 0 ? (
        <section className="card p-4 sm:p-6">
          <h2 className="text-base font-semibold text-ink">Frequently asked questions</h2>
          <div className="mt-3 divide-y divide-line">
            {faq.map((item) => (
              <details key={item.question} className="group py-3 first:pt-0 last:pb-0">
                <summary className="cursor-pointer text-sm font-medium text-ink marker:content-none">
                  <span className="mr-2 inline-block text-muted transition-transform group-open:rotate-90">
                    ▸
                  </span>
                  {item.question}
                </summary>
                <p className="mt-2 pl-5 text-sm leading-relaxed text-muted">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
